import { ReactNode, useEffect } from 'react';
import { LoaderFn, type Route, useMatch, useNavigate } from '@tanstack/react-location';
import { QueryClient } from 'react-query';
import { authnClient } from '@frontend/auth';
import { getLoginData, getUser, getWeaveToken, isWeaveTokenBufferActive, PortalUser } from '@frontend/auth-helpers';
import { ErrorPage } from '@frontend/error-boundary';
import { http } from '@frontend/fetch';
import {
  RouteWithPath,
  RouteOptions,
  getRoutesFromPaths,
  WeaveLoaderData,
  defaultMissingModuleFallback,
  defaultModuleFetchErrorFallback,
} from '@frontend/file-based-routing';
import { useHistoryControl } from '@frontend/history';
import { useTranslation } from '@frontend/i18n';
import { AllPathsMap, settingsRouteLookup, useSettingsPathLabelMap } from '@frontend/settings-routing';
import { extractAndFormatPathName } from '@frontend/string';
import { sentry } from '@frontend/tracking';
import { theme } from '@frontend/theme';
import { ContentLoader, emptyStateGraphics } from '@frontend/design-system';
import { usePrimaryNavContent } from '../../layout/global-nav/nav-content';
import NotFoundPage from '../../pages/404';
import { useSettingsNavItems } from '../use-settings-nav-items';

type Options = {
  queryClient?: QueryClient;
  missingModuleFallback: RouteOptions['missingModuleFallback'];
  moduleFetchErrorFallback: RouteOptions['moduleFetchErrorFallback'];
};

export function getWeaveRoutes(options: Options) {
  const paths = import.meta.glob<boolean, string, { [key: string]: any }>(['/src/pages/**/[a-z[]*.tsx']);

  const routes = getRoutesFromPaths(paths, {
    missingModuleFallback: options.missingModuleFallback,
    moduleFetchErrorFallback: options.moduleFetchErrorFallback,
    AccessControl: AuthenticatedRoute,
    modifyProps: (routeMatch, opts) => ({ match: { routeMatch, opts }, queryClient: options.queryClient }),
  })
    .filter((route): route is RouteWithPath => !!route.path)
    .map((route) => {
      if (route.path.startsWith('/public')) {
        return { ...route, meta: { type: 'public' as const }, path: route.path.replace('/public', '') };
      } else {
        return { ...route, meta: { type: 'private' as const } };
      }
    })
    .filter((route) => {
      return import.meta.env.MODE === 'development' ? true : !route.path?.startsWith('portal/example');
    })
    .concat([{ path: '*', meta: { type: 'public' }, element: <NotFoundPage /> }])
    .map((route) => {
      return {
        ...route,
        loader,
      };
    });
  return routes;
}

export function getWeaveSettingsRoutes(options: Options) {
  const paths = import.meta.glob<boolean, string, { [key: string]: any }>('/src/pages/settings/**/[a-z[]*.tsx');
  const routes = getRoutesFromPaths(paths, {
    missingModuleFallback: options.missingModuleFallback,
    moduleFetchErrorFallback: options.moduleFetchErrorFallback,
    AccessControl: AuthenticatedRoute,
    modifyProps: (routeMatch, opts) => ({ match: { routeMatch, opts }, queryClient: options.queryClient }),
  })
    .filter((route): route is RouteWithPath => !!route.path)
    .map((route) => {
      if (route.path.startsWith('/public')) {
        return { ...route, meta: { type: 'public' as const }, path: route.path.replace('/public', '') };
      } else {
        return { ...route, meta: { type: 'private' as const } };
      }
    })
    .concat([{ path: '*', meta: { type: 'public' }, element: <NotFoundPage /> }])
    .map((route) => {
      const path = route.path.startsWith('/settings') ? route.path.replace('/settings', '') : route.path;

      return {
        ...route,
        path,
        loader,
      };
    });
  return routes;
}

const getFlattenedWeaveSettingsRoutes = () => {
  const weaveRoutes = getWeaveRoutes({
    queryClient: undefined,
    missingModuleFallback: defaultMissingModuleFallback,
    moduleFetchErrorFallback: defaultModuleFetchErrorFallback,
  });

  const isValidRoute = (path: string) => {
    const cleanedPath = path.replace('/', '');
    return cleanedPath.startsWith('settings/');
  };

  const flattenRoutes = (routes: Route[], prefix = ''): string[] => {
    return routes.reduce((acc, route) => {
      if (!route.path) return acc;

      const fullPath = prefix + route.path;
      if (isValidRoute(fullPath)) {
        acc.push(route.path.endsWith('/') ? fullPath : fullPath + '/');
      }
      if (route.children) {
        acc.push(...flattenRoutes(route.children, route.path));
      }
      return acc;
    }, [] as string[]);
  };

  return flattenRoutes(weaveRoutes as Route[]);
};

export const buildMappedRoutesLookup = (pathLabelMap: AllPathsMap) => {
  const allRoutes = getFlattenedWeaveSettingsRoutes();
  settingsRouteLookup.build(allRoutes);

  const pathLabelCache: Record<string, string> = {};

  const mappedRoutes = allRoutes.reduce((accumulator: { labelPath: string; path: string }[], path) => {
    if (path.includes(':')) return accumulator;

    const foundPaths = settingsRouteLookup.searchPath(path);

    if (foundPaths.length <= 2) {
      const route = foundPaths
        .map((foundPath) => {
          if (!pathLabelCache[foundPath]) {
            pathLabelCache[foundPath] =
              (pathLabelMap[foundPath.replace('/settings', '') as keyof typeof pathLabelMap]?.label as string) ??
              extractAndFormatPathName(foundPath);
          }
          return pathLabelCache[foundPath];
        })
        .join('/');

      accumulator.push({ labelPath: route, path });
    }

    return accumulator;
  }, []);

  return mappedRoutes;
};

const loader: LoaderFn<
  WeaveLoaderData<{
    LoaderData: { authenticated: boolean; user: PortalUser; latestLocationId: string };
  }>
> = async () => {
  const token = getWeaveToken();
  http.setAuthorizationHeader(token);
  const authenticated = authnClient.isUserAuthenticated() || isWeaveTokenBufferActive();
  const user: PortalUser | undefined = token ? getUser() ?? (await authnClient.fetchUser()) : undefined;
  const latestLocationId = user ? getLoginData(user.userID)?.latestLocationIds?.[0] : undefined;

  /**
   * Data returned here will be available on each route, accessible via `useMatch`
   * We could test for page access here as well (authenticated but not authorized)
   */
  return { authenticated, user, latestLocationId };
};

export const AuthenticatedRoute = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    data: { authenticated, user, lastLocationId },
  } = useMatch();
  const signInUri = '/sign-in';

  useEffect(() => {
    const excludedRoutes = ['/sign-in', '/', 'sign-in/callback'];
    if (!authenticated && !excludedRoutes.includes(window.location.pathname)) {
      const originalURI = window.location.pathname + window.location.search;

      console.log(`Hit a private route "${originalURI}" without being authenticated. Redirecting to ${signInUri}`);
      navigate({
        to: signInUri,
      });
    }
  }, [authenticated]);

  if (!user) {
    sentry.log({ message: `No active user when loading authenticated route for location: ${lastLocationId}` });
    return <ContentLoader message={t('Waiting for active user...')} show />;
  }

  return authenticated ? <ProtectedRoute>{children}</ProtectedRoute> : null;
};

const useCurrentNavItem = () => {
  const appRoutes = usePrimaryNavContent();
  const settingsRoutes = useSettingsNavItems();
  const subSettingsRoutes = useSettingsPathLabelMap();
  const { pathname } = useMatch();

  /**
   * This variable determines if the user is **authorized** to access the route (based on ACLs).
   * This is not the same as being **authenticated** (logging into the app)
   */

  if (pathname.startsWith('/settings/')) {
    const path = (pathname.endsWith('/') ? pathname.slice(0, -1) : pathname).replace('/settings', '');

    return (
      settingsRoutes.find((route) => route.path === path) ?? subSettingsRoutes[path as keyof typeof subSettingsRoutes]
    );
  }

  return appRoutes.find((route) => route?.root === pathname);
};

const ProtectedRoute = ({ children }: { children: ReactNode }) => {
  const currentRoute = useCurrentNavItem();

  if (currentRoute && 'show' in currentRoute) {
    /**
     * This needs to look for an exact `false` match
     * BECAUSE undefined = showable
     */
    return currentRoute.show === false ? <NoAccess /> : <>{children}</>;
  }

  return <>{children}</>;
};

export const NoAccess = () => {
  const { t } = useTranslation();
  const { back } = useHistoryControl();
  const EmptyStateGraphicFax = emptyStateGraphics.fax;

  return (
    <ErrorPage
      title={t('Hmm...')}
      subTitle={t(
        "It looks like you don't have permission to view this page. Please contact your system administrator for more information."
      )}
      primaryButtonText={t('Previous Page')}
      primaryButtonOnClick={() => back()}
    >
      <EmptyStateGraphicFax css={{ maxHeight: 300, marginBottom: theme.spacing(4) }} />
    </ErrorPage>
  );
};
